import React from 'react';
import { Link } from 'react-router-dom';
import type { ForgotPasswordProps } from './ForgotPassword.types';
import { useForgotPassword } from './ForgotPassword.hook';
import * as styles from './ForgotPassword.styles';
import { getLangTitle } from './ForgotPassword.utils';

export const ForgotPassword: React.FC<ForgotPasswordProps> = () => {
  const {
    t,
    i18n,
    email,
    setEmail,
    message,
    errorMessage,
    loading,
    handleSubmit,
    changeLanguage,
  } = useForgotPassword();

  return (
    <div className={styles.container}>
      {/* Background glow effects */}
      <div className={styles.bgGlowPrimary}></div>
      <div className={styles.bgGlowAccent}></div>

      {/* Main glass card container */}
      <div className={styles.glassCard}>
        
        {/* Language switches & Header */}
        <div className={styles.headerContainer}>
          <div className={styles.headerLeft}>
            <img 
              src="/logo.svg" 
              alt="WealthIQ Icon" 
              className={styles.logoImg} 
            />
            <span className={styles.titleText}>
              Wealth<span className={styles.titleHighlight}>
                IQ
                <span className={styles.titleBadge}>
                  Pro
                </span>
              </span>
            </span>
          </div>

          {/* Translation Controls */}
          <div className={styles.langControlsContainer}>
            {(['en', 'hi', 'te', 'pa'] as const).map((lang) => {
              const isActive = (i18n.language || 'en').startsWith(lang);
              return (
                <button
                  key={lang}
                  onClick={() => changeLanguage(lang)}
                  className={styles.getLangBtnClass(isActive)}
                  title={getLangTitle(lang)}
                >
                  {lang}
                </button>
              );
            })}
          </div>
        </div>

        {/* Heading */}
        <div className={styles.headingContainer}>
          <h2 className={styles.headingTitle}>
            {t('forgotPasswordTitle', 'Reset Password')}
          </h2>
          <p className={styles.headingSubtitle}>
            {t('forgotPasswordSubtitle', 'Enter your email to receive a password reset link.')}
          </p>
        </div>

        {/* Status Messages */}
        {errorMessage && (
          <div className={styles.errorContainer}>
            <i className="fa-solid fa-triangle-exclamation"></i>
            <span>{errorMessage}</span>
          </div>
        )}

        {message && (
          <div className={styles.successContainer}>
            <i className="fa-solid fa-circle-check"></i>
            <span>{message}</span>
          </div>
        )}

        {/* Forgot Password Form */}
        <form onSubmit={handleSubmit} className={styles.formContainer}>
          <div className={styles.inputGroup}>
            <label htmlFor="forgot-email" className={styles.label}>
              {t('email', 'Email Address')}
            </label>
            <div className={styles.inputWrapper}>
              <i className={`${styles.iconClass} fa-envelope`}></i>
              <input
                id="forgot-email"
                type="email"
                placeholder="developer@wealthiq.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={styles.inputField}
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={styles.submitButton}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-circle-notch animate-spin"></i>
                <span>{t('sendingLink', 'Sending...')}</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane"></i>
                <span>{t('sendResetLink', 'Send Reset Link')}</span>
              </>
            )}
          </button>
        </form>

        <div className={styles.backLinkContainer}>
          <Link to="/login" className={styles.backLinkClass}>
            {t('backToLogin', 'Back to Sign In')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
